import { inject, Injectable } from '@angular/core';
import { WW_NOTES_COLLECTION } from '@damofire/data-commons';
import { AuthDtoSignerService } from '@damofire/data-security';
import { Issue } from '../issue/issue.dto';
import { getIssueDescription, NoteService } from './note.service';
import { Note } from './note.dto';
import { deleteDoc, doc, Firestore } from '@angular/fire/firestore';
import { Asset } from '../asset/asset.dto';
import { Inspection } from '../inspection/inspection.dto';
import { getEmbeddedAsset } from '../asset/asset.service';

@Injectable()
export class FirebaseNoteService extends NoteService {
  private readonly firestore = inject(Firestore);

  constructor(private readonly dtoSigner: AuthDtoSignerService) {
    super();
  }

  async create(notes: Note[]): Promise<void> {
    await this.dtoSigner.setAll(notes, WW_NOTES_COLLECTION);
  }

  async createForAsset(note: string, asset: Asset, inspection: Inspection): Promise<void> {
    const create: Note = {
      text: note,
      inspection: { _id: inspection._id.toString(), name: inspection.name },
      asset: getEmbeddedAsset(asset),
      division: inspection.division,
      organization: inspection.organization,
    };
    await this.dtoSigner.add(create, WW_NOTES_COLLECTION);
  }

  async createForIssue(note: string, issue: Issue): Promise<void> {
    if (!issue.inspection) {
      throw new Error('Missing inspection');
    }

    const create: Note = {
      text: note,
      inspection: { _id: issue.inspection._id.toString(), name: issue.inspection.name },
      asset: { ...issue.asset, _id: issue.asset._id.toString() },
      issue: { _id: issue._id, ...getIssueDescription(issue), warehouseIssueId: issue.warehouseIssueId },
      division: issue.division,
      organization: issue.organization,
    };
    await this.dtoSigner.add(create, WW_NOTES_COLLECTION);
  }

  async createForIssueBatch(notes: string[], issue: Issue): Promise<void> {
    if (!issue.inspection) {
      throw new Error('Missing inspection');
    }

    const create: Partial<Note> = {
      inspection: { _id: issue.inspection._id.toString(), name: issue.inspection.name },
      asset: { ...issue.asset, _id: issue.asset._id.toString() },
      issue: { _id: issue._id, ...getIssueDescription(issue), warehouseIssueId: issue.warehouseIssueId },
      division: issue.division,
      organization: issue.organization,
    };

    const notesToCreate: Note[] = notes.map((note) => ({ ...create, text: note }));
    await this.dtoSigner.addAll(notesToCreate, WW_NOTES_COLLECTION);
  }

  async delete(division: string, id: string): Promise<void> {
    return deleteDoc(doc(this.firestore, `${WW_NOTES_COLLECTION}/${id}`));
  }

  async update(note: Note, text: string): Promise<void> {
    await this.dtoSigner.update({ ...note, text }, `${WW_NOTES_COLLECTION}/${note._id}`);
  }
}
